import React from "react";
import { Monitor, CreditCard, Wifi, RefreshCw, Zap, ShoppingCart, Package, Play } from "lucide-react";
import { PageHero, Section, Container, Eyebrow, H2, Lead, CTAButton, GlassCard, Orbs } from "../components/PageUI";
import SEO from "../components/SEO";

const methods = ["NFC / Tap","Chip & PIN","QR Code","Magnetic Stripe","Apple Pay","Google Pay"];

const steps = [
  { icon: ShoppingCart, title: "Choose to buy or rent", desc: "Buy your kiosk outright, or rent it month to month, whichever fits your masjid’s budget." },
  { icon: Package, title: "Receive & mount", desc: "Kiosk ships pre-configured. Place it as a freestanding unit in your lobby or entrance in minutes." },
  { icon: Play, title: "Go live", desc: "Dashboard and mobile app connect automatically. Start accepting donations immediately." },
];

const features = [
  { icon: Monitor, title: '27" HD Touchscreen', desc: "Bright, responsive display, readable in all lighting conditions." },
  { icon: CreditCard, title: "Omni-Channel Payments", desc: "Accept every payment method your community prefers." },
  { icon: Wifi, title: "Always Connected", desc: "Wi-Fi with optional 4G LTE backup keeps the kiosk online." },
  { icon: RefreshCw, title: "App + Dashboard Sync", desc: "Every donation appears instantly on the admin dashboard and mobile app." },
  { icon: Zap, title: "Plug-and-Play Setup", desc: "Live in under an hour. No IT team required." },
];

const specs = [
  ["Display",'27" Full HD Touchscreen'],
  ["Processor","Quad-core 1.8 GHz"],
  ["Memory","4 GB RAM / 32 GB Storage"],
  ["OS","Android 11 (hardened kiosk mode)"],
  ["Payments","NFC, Chip & PIN, QR Code, Magnetic Stripe"],
  ["Connectivity","Wi-Fi 802.11ac + Optional 4G LTE"],
  ["Mounting","Freestanding Pedestal"],
  ["Dimensions",'22" W x 62" H x 16" D, approx. 65 lbs'],
  ["Power","Standard 110V / 220V outlet"],
  ["Operating Hours","24 / 7 continuous operation"],
];

export default function Kiosk() {
  return (
    <div>
      <SEO
        title={'27" Digital Donation Kiosk | Masjid Suite'}
        description="A purpose-built touchscreen donation kiosk for your masjid lobby. Accepts cards, tap, and mobile wallets. Buy outright or rent month to month."
        path="/kiosk"
      />
      <PageHero
        eyebrow="Masjid Suite Kiosk"
        title="The Modern Digital Kiosk"
        gradient="for Masjids."
        lead={<>A purpose-built kiosk that connects your community to your masjid: accepting donations, showing prayer times, managing programs, and more. The software that runs the kiosk is free. The kiosk hardware itself is not, it’s a one-time purchase or a monthly rental.</>}
        cta={<><CTAButton to="/contact">Get Started Free</CTAButton><CTAButton to="#specs" variant="secondary">View Specs</CTAButton></>}
      />

      <div className="bg-section-alt border-y border-[#e6e7eb] py-6">
        <Container>
          <div className="flex flex-wrap items-center justify-center gap-3 text-[13px] text-[#4b5563]">
            <span className="mr-2">Accepts every way your community gives:</span>
            {methods.map((m,i)=>(<span key={i} className="px-3 py-1 rounded-md glass-light text-xs font-semibold text-[#1d1d1f]">{m}</span>))}
          </div>
        </Container>
      </div>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="flex justify-center">
            <GlassCard hover={false} className="p-8 max-w-md w-full">
              <div className="aspect-square rounded-2xl bg-white flex items-center justify-center relative overflow-hidden border border-white/70 p-6">
                <img src="/assets/kiosk.png" alt="27&quot; Digital Donation Kiosk" className="max-w-full max-h-full object-contain" draggable={false} style={{ filter: "drop-shadow(0 12px 20px rgba(14,36,8,0.15))" }} />
              </div>
              <div className="mt-4 text-center text-[12px] text-[#6b7280] uppercase tracking-widest">27″ Digital Donation Kiosk</div>
            </GlassCard>
          </div>
        </Container>
      </Section>

      <Section className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="text-center mb-14">
            <H2 gradient="in 3 Steps">Up and Running</H2>
            <div className="mt-6 flex justify-center"><Lead>From order to live kiosk in under a week.</Lead></div>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {steps.map((s,i)=>{ const Icon=s.icon; return (
              <GlassCard key={i}>
                <div className="text-[#a8862f] font-display text-4xl font-semibold mb-3">0{i+1}</div>
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center mb-4"><Icon size={20}/></div>
                <h3 className="font-display text-xl text-[#1d1d1f] font-semibold mb-2">{s.title}</h3>
                <p className="text-[14px] text-[#4b5563] leading-relaxed">{s.desc}</p>
              </GlassCard>
            );})}
          </div>
        </Container>
      </Section>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="text-center mb-14">
            <H2 gradient="Designed for People.">Built for Masjids.</H2>
            <div className="mt-6 flex justify-center"><Lead>Every detail engineered for the unique needs of Islamic communities.</Lead></div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f,i)=>{ const Icon=f.icon; return (
              <GlassCard key={i}>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center mb-4 shadow-md"><Icon size={22}/></div>
                <h3 className="font-display text-lg text-[#1d1d1f] font-semibold mb-2">{f.title}</h3>
                <p className="text-[13px] text-[#4b5563] leading-relaxed">{f.desc}</p>
              </GlassCard>
            );})}
          </div>
        </Container>
      </Section>

      <Section id="specs" className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="text-center mb-14">
            <Eyebrow>Specs</Eyebrow>
            <H2 gradient="Specifications">Technical</H2>
            <div className="mt-6 flex justify-center"><Lead>Enterprise-grade hardware built for daily, long-term use.</Lead></div>
          </div>
          <GlassCard hover={false} className="p-0 overflow-hidden">
            <div className="divide-y divide-emerald-100">
              {specs.map(([k,v],i)=>(
                <div key={i} className="grid grid-cols-3 gap-4 px-6 py-4 text-[14px]">
                  <div className="text-[#6b7280] font-medium">{k}</div>
                  <div className="col-span-2 text-[#1d1d1f]">{v}</div>
                </div>
              ))}
            </div>
          </GlassCard>
        </Container>
      </Section>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="glass-frosted rounded-[40px] p-10 md:p-14 text-center shadow-apple-lg">
            <H2 gradient="to your masjid?">Ready to bring the Kiosk</H2>
            <div className="mt-6 flex justify-center"><Lead>Join masjid transforming their communities with modern technology. Our team will guide you every step of the way.</Lead></div>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <CTAButton to="/contact">Get Started Free</CTAButton>
              <CTAButton to="/pricing" variant="secondary">View Hardware & Pricing</CTAButton>
            </div>
          </div>
        </Container>
      </Section>
    </div>
  );
}
